import { apiFetch, getAccessToken } from '@/lib/api-client';
import type {
  PaginationMeta,
  TicketListItem,
  TicketDetail,
  OrderActionDto,
  CreateTicketRequest,
  UpdateTicketRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  DmpAddressResponse,
  DmpBuildingKeyResponse,
  DmpOrderResponse,
  DmpAddressSearchResponse,
  DmpUtacLookupResponse,
  EligibilityCheckResult,
  SplitterItem,
  SplitterOption,
  AreaOption,
  AreaNetworkResponse,
  BuildingGroupLookupResponse,
  BuildingGroupComputeResponse,
  BuildingGroupEntry,
  InternalUserItem,
  NotificationItem,
  UnreadCountResponse,
  SlaConfigItem,
  CreateSlaConfigRequest,
  UpdateSlaConfigRequest,
  OperatorListItem,
  GroupFilterItem,
  MentionableUser,
  TicketEnrichmentResponse,
  OrderWorkflowEntry,
  CoconNetworkResponse,
  SavedFilterItem,
  SaveFilterRequest,
  DashboardStatsResponse,
  DashboardActivityItem,
  TicketSearchItem,
  BulkUpdateRequest,
  BulkUpdateResponse,
  SimilarTicketsRequest,
  SimilarTicketItem,
} from '@uniticket/shared';

export async function fetchTickets(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: TicketListItem[]; meta: PaginationMeta }>(
    `/tickets${query ? `?${query}` : ''}`,
  );
}

/**
 * Fetch ALL tickets matching the current filters (no 100-row page cap) for CSV export.
 * Same filter params as fetchTickets; the backend ignores cursor/limit for exports.
 */
export async function fetchTicketsExport(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: TicketListItem[]; meta: { total: number } }>(
    `/tickets/export${query ? `?${query}` : ''}`,
  );
}

export async function fetchTicket(id: string) {
  return apiFetch<{ data: TicketDetail }>(`/tickets/${id}`);
}

export async function fetchTicketEnrichment(id: string) {
  return apiFetch<{ data: TicketEnrichmentResponse }>(`/tickets/${id}/enrichment`);
}

export async function fetchOrderWorkflow(ticketId: string, orderId: number) {
  return apiFetch<{ data: OrderWorkflowEntry[] }>(`/tickets/${ticketId}/enrichment/orders/${orderId}/workflow`);
}

export async function fetchCoconNetwork(ticketId: string, bbox: string) {
  return apiFetch<{ data: CoconNetworkResponse }>(
    `/tickets/${ticketId}/enrichment/network?bbox=${encodeURIComponent(bbox)}`,
  );
}

export async function createTicketApi(data: CreateTicketRequest) {
  return apiFetch<{ data: TicketListItem }>('/tickets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface PerFileUploadError {
  fileName: string;
  message: string;
}

export interface CreateTicketWithExtrasResult {
  ticket: TicketListItem;
  uploadErrors: PerFileUploadError[];
  /** True if the pasted customer email text could not be saved as a text attachment. */
  textAttachmentFailed: boolean;
}

/**
 * Create a ticket, then sequentially upload any staged files and persist any
 * pasted customer email as a TEXT attachment. Per-item failures are surfaced
 * via the returned arrays — the created ticket is not rolled back.
 */
export async function createTicketWithExtras(
  data: CreateTicketRequest,
  files: File[],
  pastedEmail: string,
): Promise<CreateTicketWithExtrasResult> {
  const created = await createTicketApi(data);
  const ticketId = created.data.id;

  const uploadErrors: PerFileUploadError[] = [];
  for (const file of files) {
    try {
      await uploadAttachment(ticketId, file);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      uploadErrors.push({ fileName: file.name, message });
    }
  }

  let textAttachmentFailed = false;
  const pasted = pastedEmail.trim();
  if (pasted) {
    try {
      await createTextAttachment(ticketId, pasted);
    } catch {
      textAttachmentFailed = true;
    }
  }

  return { ticket: created.data, uploadErrors, textAttachmentFailed };
}

export async function createTextAttachment(ticketId: string, textContent: string) {
  return apiFetch<{ data: TicketDetail }>(`/tickets/${ticketId}/attachments/text`, {
    method: 'POST',
    body: JSON.stringify({ textContent }),
  });
}

export async function updateTicketApi(id: string, data: UpdateTicketRequest) {
  return apiFetch<{ data: TicketListItem }>(`/tickets/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteTicketApi(id: string) {
  return apiFetch<{ message: string }>(`/tickets/${id}`, {
    method: 'DELETE',
  });
}

/** Re-run the Unifiber Ordering API call for a ticket's latest order action (internal/admin only).
 *  `runAsOperator` is required only when the ticket's own operator has no configured credentials. */
export async function retryOrderActionApi(ticketId: string, runAsOperator?: string) {
  return apiFetch<{ data: OrderActionDto }>(`/tickets/${ticketId}/order/retry`, {
    method: 'POST',
    body: JSON.stringify(runAsOperator ? { runAsOperator } : {}),
  });
}

/** Run-context for the manual-run dialog: configured operator codes (e.g. ['PROX']) + the current
 *  effective ordering mode (for the first-run LIVE/DRY_RUN warning). Internal only. */
export async function fetchCredentialedOperators() {
  return apiFetch<{ data: { operators: string[]; mode: 'LIVE' | 'DRY_RUN' | 'OFF' } }>('/orders/credentialed-operators');
}

export async function fetchDmpAddress(jvid: string) {
  return apiFetch<{ data: DmpAddressResponse }>(`/dmp/addresses/${jvid}`);
}

export async function fetchDmpAddressesByBuildingKey(buildingId: string) {
  return apiFetch<{ data: DmpBuildingKeyResponse }>(`/dmp/addresses/building/${buildingId}`);
}

export async function searchDmpAddresses(params: { street?: string; postalCode?: string; houseNumber?: string }) {
  const qs = new URLSearchParams();
  if (params.street) qs.set('street', params.street);
  if (params.postalCode) qs.set('postalCode', params.postalCode);
  if (params.houseNumber) qs.set('houseNumber', params.houseNumber);
  return apiFetch<{ data: DmpAddressSearchResponse }>(`/dmp/addresses/search?${qs}`);
}

export async function fetchDmpOrder(jvid: string) {
  return apiFetch<{ data: DmpOrderResponse; orders: DmpOrderResponse[] }>(`/dmp/orders/${jvid}`);
}

export async function fetchDmpAddressByUtac(utac: string) {
  return apiFetch<{ data: DmpUtacLookupResponse }>(`/dmp/addresses/utac/${encodeURIComponent(utac)}`);
}

export async function checkEligibility(identifier: string, subcategory: string, mode: 'jvid' | 'utac') {
  const path = mode === 'utac'
    ? `/dmp/eligibility/utac/${encodeURIComponent(identifier)}?subcategory=${subcategory}`
    : `/dmp/eligibility/${identifier}?subcategory=${subcategory}`;
  return apiFetch<{ data: EligibilityCheckResult }>(path);
}

export async function searchSplitters(query: string, limit = 50) {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return apiFetch<{ data: SplitterItem[] }>(`/dmp/splitters?${params}`);
}

/** Splitter Swap — the splitters DMP currently documents for this JVID ("From splitter" options). */
export async function fetchDocumentedSplitters(jvid: string) {
  return apiFetch<{ data: SplitterOption[] }>(`/dmp/splitters/from/${jvid}`);
}

/**
 * Splitter Swap — candidates for the "To splitter" pre-fill and the typed search on BOTH fields.
 * An empty `query` returns the siblings of the JVID's documented reference; a non-empty one
 * searches the caller's whole operator-scoped splitter list.
 */
export async function searchSwapSplitters(jvid: string, query: string, limit = 50) {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return apiFetch<{ data: SplitterOption[] }>(`/dmp/splitters/search/${jvid}?${params}`);
}

/**
 * Build wizard — DMP areas for a subcategory, optionally narrowed by a search term.
 * An empty `term` returns the subcategory's whole (small) area list, so the picker is browsable.
 */
export async function searchBuildAreas(subcategory: string, term: string) {
  const params = new URLSearchParams({ subcategory, term });
  return apiFetch<{ data: AreaOption[] }>(`/dmp/areas/search?${params}`);
}

/** Full DMP enrichment for one JVID attached to a ticket (404 unless it is actually attached). */
export async function fetchAttachedJvidEnrichment(ticketId: string, jvid: number) {
  return apiFetch<{ data: TicketEnrichmentResponse }>(`/tickets/${ticketId}/jvids/${jvid}/enrichment`);
}

export async function attachJvid(ticketId: string, jvid: number) {
  return apiFetch<{ data: number[] }>(`/tickets/${ticketId}/jvids`, {
    method: 'POST',
    body: JSON.stringify({ jvid }),
  });
}

export async function detachJvid(ticketId: string, jvid: number) {
  return apiFetch<{ data: number[] }>(`/tickets/${ticketId}/jvids/${jvid}`, { method: 'DELETE' });
}

/** Build ticket map — the area outline, the viewport, and the network layers around it. */
export async function fetchAreaNetwork(areaId: string) {
  return apiFetch<{ data: AreaNetworkResponse }>(`/dmp/areas/${encodeURIComponent(areaId)}/network`);
}

export async function fetchBuildingGroup(buildingId: string) {
  return apiFetch<{ data: BuildingGroupLookupResponse }>(`/dmp/building-group/${buildingId}`);
}

export async function computeBuildingGroupVolume(buildingId: string, selectedMainAddress: string) {
  return apiFetch<{ data: BuildingGroupComputeResponse }>(`/dmp/building-group/${buildingId}/compute`, {
    method: 'POST',
    body: JSON.stringify({ selectedMainAddress }),
  });
}

export async function fetchBuildingVolumes(buildingId: string) {
  return apiFetch<{ data: { buildingId: number; entries: BuildingGroupEntry[]; sduDefault: { volumeMdu: number; existingBoxes: string } | null; fetchedAt: string } }>(
    `/dmp/addresses/building/${buildingId}/volumes`, { method: 'POST' },
  );
}

export async function fetchJvidVolumes(jvid: string) {
  return apiFetch<{ data: { jvid: number; buildingId: number | null; entries: BuildingGroupEntry[]; sduDefault: { volumeMdu: number; existingBoxes: string } | null; fetchedAt: string } }>(
    `/dmp/addresses/${jvid}/volumes`,
  );
}

export async function fetchInternalUsers() {
  return apiFetch<{ data: InternalUserItem[] }>('/users/internal');
}

export async function fetchAllUsers() {
  return apiFetch<{ data: InternalUserItem[] }>('/users/all');
}

export async function fetchMentionableUsers() {
  return apiFetch<{ data: MentionableUser[] }>('/users/mentionable');
}

// ── Attachments ──────────────────────────────────────────

export async function uploadAttachment(ticketId: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return apiFetch<{ data: TicketDetail }>(`/tickets/${ticketId}/attachments`, {
    method: 'POST',
    body: formData,
  });
}

export async function downloadAttachment(attachmentId: string): Promise<Blob> {
  const token = getAccessToken();
  const res = await fetch(`/internal/v1/attachments/${attachmentId}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message || 'Download failed');
  }
  return res.blob();
}

export async function deleteAttachmentApi(attachmentId: string) {
  return apiFetch<{ data: TicketDetail }>(`/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
}

// ── Comments ──────────────────────────────────────────────

export async function postComment(ticketId: string, data: CreateCommentRequest) {
  return apiFetch<{ data: TicketDetail }>(`/tickets/${ticketId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function editComment(ticketId: string, commentId: string, data: UpdateCommentRequest) {
  return apiFetch<{ data: TicketDetail }>(`/tickets/${ticketId}/comments/${commentId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteCommentApi(ticketId: string, commentId: string) {
  return apiFetch<{ data: TicketDetail }>(`/tickets/${ticketId}/comments/${commentId}`, {
    method: 'DELETE',
  });
}

// ── Watchers ──────────────────────────────────────────────

export async function addWatcherApi(ticketId: string, userId: string) {
  return apiFetch<{ data: TicketDetail }>(`/tickets/${ticketId}/watchers`, {
    method: 'POST',
    body: JSON.stringify({ userId }),
  });
}

export async function removeWatcherApi(ticketId: string, userId: string) {
  return apiFetch<{ data: TicketDetail }>(`/tickets/${ticketId}/watchers/${userId}`, {
    method: 'DELETE',
  });
}

// ── Notifications ─────────────────────────────────────────

export async function fetchUnreadCount() {
  return apiFetch<UnreadCountResponse>('/notifications/unread-count');
}

export async function fetchNotifications(params: Record<string, string> = {}) {
  const query = new URLSearchParams(params).toString();
  return apiFetch<{ data: NotificationItem[]; meta: { hasMore: boolean; cursor: string | null } }>(
    `/notifications${query ? `?${query}` : ''}`,
  );
}

export async function markNotificationRead(id: string) {
  return apiFetch<{ message: string }>(`/notifications/${id}/read`, {
    method: 'PATCH',
  });
}

export async function markAllNotificationsRead() {
  return apiFetch<{ message: string; count: number }>('/notifications/read-all', {
    method: 'PATCH',
  });
}

export async function clearNotification(id: string) {
  return apiFetch<{ message: string }>(`/notifications/${id}/clear`, {
    method: 'POST',
  });
}

export async function clearAllNotifications() {
  return apiFetch<{ message: string; count: number }>('/notifications/clear-all', {
    method: 'POST',
  });
}

// ── SLA Configs ──────────────────────────────────────────

export async function fetchSlaConfigs() {
  return apiFetch<{ data: SlaConfigItem[] }>('/admin/sla-configs');
}

export async function createSlaConfigApi(data: CreateSlaConfigRequest) {
  return apiFetch<{ data: SlaConfigItem }>('/admin/sla-configs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSlaConfigApi(id: string, data: UpdateSlaConfigRequest) {
  return apiFetch<{ data: SlaConfigItem }>(`/admin/sla-configs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteSlaConfigApi(id: string) {
  return apiFetch<{ message: string }>(`/admin/sla-configs/${id}`, {
    method: 'DELETE',
  });
}

// ── Operators ────────────────────────────────────────────

export async function fetchOperators() {
  return apiFetch<{ data: OperatorListItem[] }>('/admin/operators');
}

export async function fetchOperatorsForFilter() {
  return apiFetch<{ data: OperatorListItem[] }>('/operators');
}

export async function fetchGroupsForFilter() {
  return apiFetch<{ data: GroupFilterItem[] }>('/groups');
}

// ── Saved Filters ────────────────────────────────────────

export async function fetchSavedFilters() {
  return apiFetch<{ data: SavedFilterItem[] }>('/saved-filters');
}

export async function createSavedFilterApi(data: SaveFilterRequest) {
  return apiFetch<{ data: SavedFilterItem }>('/saved-filters', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSavedFilterApi(id: string, data: Partial<SaveFilterRequest>) {
  return apiFetch<{ data: SavedFilterItem }>(`/saved-filters/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteSavedFilterApi(id: string) {
  return apiFetch<{ message: string }>(`/saved-filters/${id}`, {
    method: 'DELETE',
  });
}

// ── Search & Bulk Operations ─────────────────────────────

export async function quickSearchTickets(q: string, limit = 8) {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return apiFetch<{ data: TicketSearchItem[] }>(`/tickets/search?${params}`);
}

export async function bulkUpdateTicketsApi(data: BulkUpdateRequest) {
  return apiFetch<BulkUpdateResponse>('/tickets/bulk-update', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Dashboard ────────────────────────────────────────────

export async function fetchDashboardStats() {
  return apiFetch<{ data: DashboardStatsResponse }>('/dashboard/stats');
}

export async function fetchDashboardActivity(limit = 10) {
  return apiFetch<{ data: DashboardActivityItem[] }>(`/dashboard/activity?limit=${limit}`);
}

// ── Similar Tickets ──────────────────────────────────────

export async function fetchSimilarTickets(data: SimilarTicketsRequest) {
  return apiFetch<{ data: SimilarTicketItem[] }>('/tickets/similar', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Tickets by JVID ──────────────────────────────────────

export async function fetchTicketsByJvid(jvid: number, excludeTicketId?: string) {
  const params = new URLSearchParams();
  if (excludeTicketId) params.set('excludeId', excludeTicketId);
  const qs = params.toString();
  return apiFetch<{ data: Array<{ id: string; ticketNumber: string; subject: string; status: string; priority: string; ticketType: string; createdAt: string; updatedAt: string }> }>(`/tickets/by-jvid/${jvid}${qs ? `?${qs}` : ''}`);
}
